/**
 * Guided product tour — spotlight overlay, no new dependencies.
 *
 * Why hand-rolled rather than react-joyride/shepherd: this portal deliberately
 * keeps its dependency surface tiny (axios + react + react-router; even
 * react-query was passed over). A tour is a positioned box, a cut-out, and an
 * index — about 150 lines. A library would be ~40kB to save those lines.
 *
 * How it works: each step names a `target` CSS selector. We measure that element
 * and render four dim panels around it (rather than a box-shadow cut-out, which
 * can't be clicked through) plus a tooltip on the nearest free side. Steps whose
 * target isn't on screen are skipped automatically, so the same tour works from
 * any page and doesn't break when a nav item is hidden by role.
 *
 * Usage:
 *   <TourProvider><App/></TourProvider>
 *   const { startTour } = useTour();
 *
 * The tour auto-runs once for a new user (localStorage flag) and can be replayed
 * any time from Help.
 */
import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SEEN_KEY = 'spectraiq:tour:seen:v1';

/**
 * The steps. `route` navigates first if we're not already there — a tour that
 * only works from the dashboard isn't much of a tour.
 *
 * Ordering follows what a new dealer actually needs to know, not the nav order:
 * where leads land → how the AI is supervised → what to set up before any of it
 * works.
 */
export const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to SpectraIQ',
    body: 'A two-minute tour of where things live. You can leave at any time — press Esc, or replay it later from Help.',
    target: null,             // centred, no spotlight
  },
  {
    id: 'leads',
    title: 'Leads land here',
    body: 'Every enquiry — SMS, WhatsApp, a phone call, a marketplace form — becomes a lead here. The AI has usually already replied by the time you look.',
    target: '[data-tour="nav-leads"]',
    route: '/leads',
  },
  {
    id: 'lead-detail',
    title: 'The whole conversation, in one thread',
    body: 'Open a lead and you see every message and call in order, whichever channel it came in on. If you want to take over from the AI, just start typing — it steps back automatically.',
    target: '[data-tour="nav-leads"]',
  },
  {
    id: 'triage',
    title: 'Triage is how you supervise the AI',
    body: 'You don’t read every conversation. Triage surfaces the ones worth reading — sentiment drops, quality flags, someone asking for a manager. Check it daily in your first week.',
    target: '[data-tour="nav-triage"]',
    route: '/triage',
  },
  {
    id: 'dead-leads',
    title: 'Lost leads get worked automatically',
    body: 'Old enquiries that went quiet get re-engaged on a cadence you control. Leave this off for your first week — get inbound working and trusted first.',
    target: '[data-tour="nav-dead-leads"]',
  },
  {
    id: 'settings',
    title: 'Start here: connect your channels',
    body: 'None of the above works until SpectraIQ can reach your phone number. Settings → Channels is where you connect Twilio and choose how WhatsApp runs.',
    target: '[data-tour="nav-settings"]',
    route: '/settings',
  },
  {
    id: 'help',
    title: 'The guides live in Help',
    body: 'Step-by-step Twilio setup, the onboarding checklist, and why WhatsApp makes you pick one provider. Replay this tour from there too.',
    target: '[data-tour="nav-help"]',
  },
];

const TourContext = createContext({ startTour: () => {}, active: false });

export function useTour() {
  return useContext(TourContext);
}

export function TourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [index, setIndex]   = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const startTour = useCallback(() => {
    setIndex(0);
    setActive(true);
  }, []);

  const stop = useCallback(() => {
    setActive(false);
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* private mode */ }
  }, []);

  // Auto-run once for a brand-new user. Deliberately not on the very first
  // paint — let the app settle so the nav targets exist to be measured.
  useEffect(() => {
    let seen = true;
    try { seen = localStorage.getItem(SEEN_KEY) === '1'; } catch { /* private mode: don't nag */ }
    if (seen) return;
    const t = setTimeout(() => setActive(true), 900);
    return () => clearTimeout(t);
  }, []);

  const value = useMemo(() => ({ startTour, active }), [startTour, active]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && (
        <TourOverlay
          index={index}
          setIndex={setIndex}
          onClose={stop}
          navigate={navigate}
          pathname={location.pathname}
        />
      )}
    </TourContext.Provider>
  );
}

/* ─────────────────────────── overlay ─────────────────────────── */

function TourOverlay({ index, setIndex, onClose, navigate, pathname }) {
  const step = TOUR_STEPS[index];
  const [rect, setRect] = useState(null);
  const tipRef = useRef(null);

  const isLast  = index === TOUR_STEPS.length - 1;
  const next    = () => (isLast ? onClose() : setIndex((i) => i + 1));
  const prev    = () => setIndex((i) => Math.max(0, i - 1));

  // Navigate if this step lives on another page.
  useEffect(() => {
    if (step?.route && pathname !== step.route) navigate(step.route);
  }, [step, pathname, navigate]);

  // Measure the target. Re-measure on resize/scroll — the sidebar can scroll.
  useLayoutEffect(() => {
    if (!step?.target) { setRect(null); return; }

    let raf;
    const measure = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        // Target not on screen (hidden by role, or a page still loading).
        // Fall back to a centred card rather than pointing at nothing.
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const pad = 6;
      setRect({
        top: r.top - pad, left: r.left - pad,
        width: r.width + pad * 2, height: r.height + pad * 2,
      });
    };

    // Give the route change a frame to land before measuring.
    raf = requestAnimationFrame(() => requestAnimationFrame(measure));
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step, pathname]);

  // Keyboard: Esc to leave, arrows to move.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!step) return null;

  const dim = 'rgba(8, 10, 14, 0.62)';

  // Tooltip position: to the right of the spotlight when there's room (the nav
  // is on the left, so that's the common case), otherwise centred.
  const tip = rect
    ? {
        top: Math.min(Math.max(12, rect.top - 8), window.innerHeight - 220),
        left: Math.min(rect.left + rect.width + 14, window.innerWidth - 360),
      }
    : {
        top: window.innerHeight / 2 - 110,
        left: window.innerWidth / 2 - 175,
      };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
      {/* Four panels around the target rather than one overlay with a hole —
          a box-shadow cut-out still swallows clicks, and we want the spotlit
          element to stay visible and un-dimmed. */}
      {rect ? (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: Math.max(0, rect.top), background: dim }} />
          <div style={{ position: 'fixed', top: rect.top + rect.height, left: 0, right: 0, bottom: 0, background: dim }} />
          <div style={{ position: 'fixed', top: rect.top, left: 0, width: Math.max(0, rect.left), height: rect.height, background: dim }} />
          <div style={{ position: 'fixed', top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height, background: dim }} />
          {/* Ring around the spotlit element */}
          <div style={{
            position: 'fixed',
            top: rect.top, left: rect.left, width: rect.width, height: rect.height,
            border: '2px solid var(--accent-primary)',
            borderRadius: 'var(--radius-sm)',
            pointerEvents: 'none',
            boxShadow: '0 0 0 3px var(--accent-glow)',
          }} />
        </>
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: dim }} onClick={onClose} />
      )}

      {/* Tooltip */}
      <div
        ref={tipRef}
        className="card"
        style={{
          position: 'fixed',
          top: tip.top, left: tip.left,
          width: 340, padding: 16,
          boxShadow: 'var(--shadow-lg, 0 12px 40px rgba(0,0,0,0.35))',
        }}
      >
        <div className="flex-between" style={{ marginBottom: 6 }}>
          <span className="mono text-xs text-muted">
            {index + 1} / {TOUR_STEPS.length}
          </span>
          <button
            className="btn small"
            style={{ padding: '2px 8px' }}
            onClick={onClose}
            aria-label="Close tour"
          >
            Skip
          </button>
        </div>

        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{step.title}</div>
        <div className="text-sm" style={{ lineHeight: 1.65, color: 'var(--text-secondary)', marginBottom: 14 }}>
          {step.body}
        </div>

        {/* Progress dots */}
        <div className="flex-between">
          <div style={{ display: 'flex', gap: 4 }}>
            {TOUR_STEPS.map((_, i) => (
              <span key={i} style={{
                width: 5, height: 5, borderRadius: '50%',
                background: i === index ? 'var(--accent-primary)' : 'var(--border-color)',
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {index > 0 && <button className="btn small" onClick={prev}>Back</button>}
            <button className="btn small primary" onClick={next}>
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
